/**
 * AuthorizeActionDialog
 *
 * The single way the app asks permission to do something. One dialog, three
 * escalating levels — a caller declares what the action *needs*, never how to
 * render it:
 *
 *   requires="confirm"    yes/no.
 *   requires="reason"     yes/no + why.
 *   requires="authorize"  yes/no + why + who. Reason is always required here.
 *
 * Why this is one component and not two stacked ones: the authorization step
 * *is* the confirmation. It already states what is about to happen and demands
 * a deliberate credential, so putting an "are you sure?" in front of it is
 * friction twice for a single decision.
 *
 * ── The two factors are NOT interchangeable ──────────────────────────────
 *
 * This matters more than it looks, and the backend draws the line, not us:
 *
 *   password — POSOrderViewSet._resolve_cancellation_approver() validates it
 *     with `request.user.check_password(...)` and then checks *request.user's*
 *     permission. It is therefore self-approval: you re-prove it's you, and
 *     you must personally hold the permission. A manager standing behind you
 *     cannot type theirs — it would be checked against your account and fail.
 *
 *   wristband — the band is resolved by `hash_nfc_uid(uid)` to a Person, then
 *     to that person's User, and *that* user's permission is checked. This is
 *     the only factor that means "somebody else authorized this".
 *
 * Self-approval is allowed on purpose: a manager alone on a night shift must
 * be able to fix a sale. The accountability comes from the reason and the
 * name landing in the audit feed, not from a second body in the room.
 *
 * ── Errors stay inside ───────────────────────────────────────────────────
 *
 * `onConfirm` is async. A rejection renders in the dialog and the operator
 * retries without retyping the reason. (The pre-existing void/refund dialogs
 * threw the password away and shouted in a toast; the comp dialog got this
 * right. We kept the comp behaviour.)
 */

import { useEffect, useState } from 'react';
import { KeyRound, Loader2, Lock, Nfc, ShieldAlert, ShieldCheck } from 'lucide-react';

import { useTranslation } from '@/core';
import { cn } from '@/lib/utils';
import { Button } from '@/theme/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/theme/components/ui/dialog';
import { Input } from '@/theme/components/ui/input';
import { Label } from '@/theme/components/ui/label';
import { Textarea } from '@/theme/components/ui/textarea';
import { useNfcStatus } from '@/shared/hooks/useNfcStatus';
import { useScanBandOnce } from '@/shared/hooks/useScanBandOnce';

/** What the action demands before it may run. */
export type AuthorizeRequires = 'confirm' | 'reason' | 'authorize';

/** Which second factors this action accepts. Defaults to both. */
export type AuthorizeFactor = 'password' | 'nfc';

export interface AuthorizeResult {
  reason?: string;
  approverPassword?: string;
  approverNfcUid?: string;
}

export interface AuthorizeActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Serif dialog title — say what will happen, not "Are you sure?". */
  title: string;
  /** Plain-language consequences. ReactNode so callers can bold the numbers. */
  description: React.ReactNode;
  /** Optional "what you're approving" block, rendered above the inputs. */
  recap?: React.ReactNode;
  /**
   * Action-specific controls, rendered between the recap and the reason.
   *
   * Exists so an action that needs its own inputs — picking a replacement
   * tender, say — stays ONE dialog. Stacking a second dialog to collect the
   * reason and credential would be the "are you sure?" mistake in a new hat.
   */
  children?: React.ReactNode;
  /** Widen the dialog when the children need more room than a recap. */
  contentClassName?: string;
  requires?: AuthorizeRequires;
  /**
   * Permission codenames shown to the operator so a denial is predictable.
   * Enforcement is server-side — this is copy, never a gate.
   */
  permissions?: string[];
  factors?: AuthorizeFactor[];
  variant?: 'default' | 'destructive';
  /**
   * Block confirm on the caller's own criteria — set when `children` collect
   * inputs the dialog can't see (an amount, a picked line). Composes with the
   * built-in reason/factor gate; it never loosens it.
   */
  confirmDisabled?: boolean;
  confirmLabel?: string;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  /** Cap the reason. Comps land in `reason_code`, a CharField(100). */
  reasonMaxLength?: number;
  /**
   * Runs the action. Rejections render inline; resolving closes the dialog.
   * The resolved value is ignored, so a mutation's `mutateAsync` can be
   * passed straight through without a wrapper.
   */
  onConfirm: (auth: AuthorizeResult) => Promise<unknown>;
}

export function AuthorizeActionDialog({
  open,
  onOpenChange,
  title,
  description,
  recap,
  requires = 'confirm',
  permissions = [],
  factors = ['password', 'nfc'],
  variant = 'destructive',
  confirmDisabled = false,
  confirmLabel,
  reasonLabel,
  reasonPlaceholder,
  reasonMaxLength,
  children,
  contentClassName,
  onConfirm,
}: AuthorizeActionDialogProps) {
  const { t } = useTranslation();
  const nfcStatus = useNfcStatus();
  // Send the same identifier every other lookup sends: the written credential,
  // falling back to the chip UID for a never-paired card. The server hashes
  // what it receives and matches it against `Wristband.nfc_uid_hash`, which is
  // built from the *stored* identifier — the credential for every paired band.
  // Asking for `prefer: 'uid'` here made the hash miss by construction, so the
  // wristband factor answered "Authorized wristband not found" every time.
  const { scanDetailed, scanning, cancel: cancelScan } = useScanBandOnce();

  const needsReason = requires === 'reason' || requires === 'authorize';
  const needsAuth = requires === 'authorize';
  const allowPassword = factors.includes('password');
  const allowNfc = factors.includes('nfc');

  const [factor, setFactor] = useState<AuthorizeFactor>(allowPassword ? 'password' : 'nfc');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset per opening — a dialog that remembers a stale password is a footgun.
  useEffect(() => {
    if (!open) return;
    setReason('');
    setPassword('');
    setError(null);
    setSubmitting(false);
    setFactor(allowPassword ? 'password' : 'nfc');
  }, [open, allowPassword]);

  const busy = submitting || scanning;

  const canSubmit =
    !busy &&
    !confirmDisabled &&
    (!needsReason || reason.trim().length > 0) &&
    (!needsAuth || factor === 'nfc' || password.trim().length > 0);

  const close = (next: boolean) => {
    if (busy) return;
    if (!next) cancelScan();
    onOpenChange(next);
  };

  const run = async (auth: AuthorizeResult) => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(auth);
      onOpenChange(false);
    } catch (err) {
      // Stay open, keep the reason, surface why.
      setError(err instanceof Error ? err.message : t('common.error', 'Something went wrong.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    const base: AuthorizeResult = needsReason ? { reason: reason.trim() } : {};

    if (!needsAuth) {
      await run(base);
      return;
    }

    if (factor === 'nfc') {
      setError(null);
      const outcome = await scanDetailed();
      if (outcome.status === 'bad_read') {
        // The band WAS tapped but the chip did not read in full — a retry
        // problem, distinct from "no band" and never a permission one.
        setError(
          t(
            'auth.approver.scanIncomplete',
            'The wristband was not read completely. Hold it flat against the reader and try again.',
          ),
        );
        return;
      }
      if (outcome.status !== 'ok') {
        setError(
          t('auth.approver.scanTimeout', 'No wristband was read. Try again, or use a password.'),
        );
        return;
      }
      await run({ ...base, approverNfcUid: outcome.uid });
      return;
    }

    await run({ ...base, approverPassword: password.trim() });
  };

  const permissionHint = permissions.length > 0 && (
    <p className="text-[11px] leading-relaxed text-muted-foreground">
      {factor === 'nfc'
        ? t('auth.approver.permissionHintNfc', 'The scanned band must belong to someone holding:')
        : t('auth.approver.permissionHintPassword', 'Your own account must hold:')}{' '}
      {permissions.map((code) => (
        <span
          key={code}
          className="mx-0.5 inline-flex rounded-sm bg-sand-100 px-1.5 py-px font-mono text-[10px] font-semibold text-sand-700"
        >
          {code}
        </span>
      ))}
    </p>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className={cn('sm:max-w-[480px]', contentClassName)}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-[19px] font-semibold tracking-[-0.01em]">
            {needsAuth ? (
              <Lock className="h-4 w-4 shrink-0 text-coral-600" aria-hidden="true" />
            ) : null}
            {title}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] leading-relaxed">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto max-h-[min(60vh,520px)] pr-1 -mr-1">
          {recap ? (
            <div className="rounded-md border border-border bg-sand-50 px-3.5 py-3 text-[12px]">
              {recap}
            </div>
          ) : null}

          {children}

          {error ? (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-md border border-coral-200 bg-coral-50 px-3.5 py-3 text-[12.5px] leading-relaxed"
            >
              <ShieldAlert className="mt-px h-4 w-4 shrink-0 text-coral-700" aria-hidden="true" />
              <span className="text-sand-900">{error}</span>
            </div>
          ) : null}

          {needsReason ? (
            <div className="space-y-2">
              <Label htmlFor="authorize-reason" className="text-[11.5px] font-semibold">
                {reasonLabel ?? t('auth.approver.reasonLabel', 'Reason')}{' '}
                <span className="text-coral-600">*</span>
              </Label>
              <Textarea
                id="authorize-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                maxLength={reasonMaxLength}
                placeholder={reasonPlaceholder ?? t('auth.approver.reasonPlaceholder', 'Why is this being changed?')}
                className="min-h-[58px] text-[12.5px]"
                autoFocus
              />
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                {t(
                  'auth.approver.reasonHelp',
                  'Recorded in Adjustments next to the approver. Write what an auditor would need a year from now.',
                )}
              </p>
            </div>
          ) : null}

          {needsAuth ? (
            <div className="space-y-2">
              <Label className="text-[11.5px] font-semibold">
                {t('auth.approver.label', 'Authorized by')} <span className="text-coral-600">*</span>
              </Label>

              {allowPassword && allowNfc ? (
                <div className="flex gap-1.5">
                  <FactorButton
                    active={factor === 'password'}
                    disabled={busy}
                    onClick={() => { setFactor('password'); setError(null); }}
                    icon={<KeyRound className="h-3.5 w-3.5" aria-hidden="true" />}
                    label={t('auth.approver.factorPassword', 'My password')}
                  />
                  <FactorButton
                    active={factor === 'nfc'}
                    disabled={busy}
                    onClick={() => { setFactor('nfc'); setError(null); }}
                    icon={<Nfc className="h-3.5 w-3.5" aria-hidden="true" />}
                    label={t('auth.approver.factorNfc', 'Scan a wristband')}
                  />
                </div>
              ) : null}

              {factor === 'password' ? (
                <>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                    placeholder={t('auth.approver.passwordPlaceholder', 'Your password')}
                    aria-label={t('auth.approver.passwordPlaceholder', 'Your password')}
                    className="h-9 text-[12.5px]"
                  />
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    {t(
                      'auth.approver.selfApprovalNote',
                      'This confirms it is really you — approving your own change is allowed, and your name and reason are recorded either way. For someone else to approve, scan their wristband.',
                    )}
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-3 rounded-md border border-dashed border-sand-200 bg-sand-50 px-4 py-3.5">
                  {scanning ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-ocean-600" aria-hidden="true" />
                  ) : (
                    <Nfc
                      className={cn(
                        'h-4 w-4 shrink-0',
                        nfcStatus === 'ready' ? 'animate-pulse text-ocean-600' : 'text-muted-foreground',
                      )}
                      aria-hidden="true"
                    />
                  )}
                  <span className="text-[12px] text-muted-foreground">
                    {scanning
                      ? t('auth.approver.scanning', 'Waiting for a wristband…')
                      : nfcStatus === 'ready'
                        ? t('auth.approver.scanReady', 'The authorizing wristband is scanned when you confirm.')
                        : t('auth.approver.noReader', 'No NFC reader available. Connect Shaka Bridge, or use a password.')}
                  </span>
                </div>
              )}

              {permissionHint}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="gap-1.5"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : needsAuth ? (
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            ) : null}
            {busy
              ? t('common.processing')
              : (confirmLabel ?? t('common.confirm'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FactorButton({
  active,
  disabled,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-[12px] transition-colors disabled:opacity-50',
        active
          ? 'border-ocean-600 bg-ocean-50 font-semibold text-ocean-700'
          : 'border-sand-200 bg-card text-muted-foreground hover:border-sand-400 hover:bg-sand-50',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
